import { useMemo } from 'react';
import { FETCH_M_SOSHIKI_DIVISIONS } from '../../queries/pdca_m_soshiki_division.queries.type';
import { getSeisakuLeaf } from '../../utils/keikakuFunctions';

type SoshikiDivision = FETCH_M_SOSHIKI_DIVISIONS['pdca_m_soshiki_division'][0] & { jigyoCount: number };
type SeisakuShisaku = FETCH_M_SOSHIKI_DIVISIONS['pdca_m_seisaku_shisaku'][0] & { jigyoCount: number };
type YosanData = {
    jigyo_year: number;
    saishutsuYosan: number;
    saishutsuKessan: number;
    sainyu: number;
    phase: string;
};

export type DashBoardData = (FETCH_M_SOSHIKI_DIVISIONS['pdca_t_pdca'][0] & { jigyoCount: number } & { yosanDatas: YosanData[] }) | null;

type UseToppagePresenter = (data: FETCH_M_SOSHIKI_DIVISIONS | undefined) => {
    soshikiDivisions: SoshikiDivision[];
    seisakuShisakus: SeisakuShisaku[];
    dashBoardDatas: DashBoardData;
};

/**
 * トップページPresenterのカスタムフック
 * @param data
 * @returns
 */
export const useToppagePresenter: UseToppagePresenter = (data) => {
    const soshikiDivisions = useMemo<SoshikiDivision[]>(() => {
        if (!data) {
            return [];
        } else {
            return data.pdca_m_soshiki_division.map((division) => {
                return {
                    ...division,
                    jigyoCount: data.pdca_t_pdca.filter((pdca) => pdca.m_soshiki_division_id_main === division.id).length,
                };
            });
        }
    }, [data]);

    // 計画の根の要素を取得
    const seisakuShisakus = useMemo<SeisakuShisaku[]>(() => {
        if (!data) {
            return [];
        } else {
            const targetKeikaku = data.pdca_m_keikaku.find((keikaku) => keikaku.keikaku_name === '総合計画');
            if (!targetKeikaku) {
                return [];
            }
            // 計画を絞り込み
            const filteredKeikakuData = data.pdca_m_seisaku_shisaku.filter(
                (seisakuShisaku) => seisakuShisaku.m_keikaku_id === targetKeikaku.id
            );
            // 最上位施策(根)を取得
            const topLevelShisaku = filteredKeikakuData.filter((seisakuShisaku) => !seisakuShisaku.parent_cd);

            return topLevelShisaku.map((shisaku) => {
                // 事業数を取得
                const jigyoDatas = data.pdca_t_pdca.filter((item) => {
                    const childrenSeisakus = getSeisakuLeaf(shisaku, filteredKeikakuData);
                    // 事業に紐づく政策を取得
                    const seisakuShisakuLinkedJigyo = item.t_jigyo.c_jimu_jigyo_seisaku_shisakus;
                    // 事業の政策に選択した政策が含まれているか
                    return childrenSeisakus.some((seisaku) => {
                        const jigyoSeisakuIds = seisakuShisakuLinkedJigyo.map((jigyoSeisaku) => jigyoSeisaku.m_seisaku_shisaku_id);
                        return jigyoSeisakuIds.includes(seisaku.id);
                    });
                });
                return {
                    ...shisaku,
                    jigyoCount: jigyoDatas.length,
                };
            });
        }
    }, [data]);

    // 事業件数を取得
    const dashBoardDatas = useMemo<DashBoardData>(() => {
        if (!data || data.pdca_t_pdca.length === 0) {
            return null;
        } else {
            const yosanDatas = [
                { jigyo_year: 2022, saishutsuYosan: 10530000000, saishutsuKessan: 12041660000, sainyu: 12752720000, phase: 'kessan' },
                { jigyo_year: 2023, saishutsuYosan: 12150310000, saishutsuKessan: 12404540000, sainyu: 12967940000, phase: 'kessan' },
                { jigyo_year: 2024, saishutsuYosan: 12526000000, saishutsuKessan: 12652370000, sainyu: 13254530000, phase: 'kessan' },
                { jigyo_year: 2025, saishutsuYosan: 13164000000, saishutsuKessan: 14071370000, sainyu: 14633740000, phase: 'kessan' },
                { jigyo_year: 2026, saishutsuYosan: 14853000000, saishutsuKessan: 0, sainyu: 14853000000, phase: 'yosan' },
            ];

            return { ...data.pdca_t_pdca[0], yosanDatas, jigyoCount: data.pdca_t_pdca.length };
        }
    }, [data]);

    return { soshikiDivisions, seisakuShisakus, dashBoardDatas };
};
